
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { aiProxy } from '../services/aiProxy';
import { UserData, EmploymentType, CommunicationLog, FeedbackLog, FeedbackCategory } from '../types';
import { logCommunication, getPublicIP, logFeedback } from '../services/dbService';
import { sendFeedbackEmail } from '../services/emailService';

// Icons
import { SpinnerIcon } from './icons/SpinnerIcon';
import { ChevronDownIcon } from './icons/ChevronDownIcon';
import { MicrophoneIcon } from './icons/MicrophoneIcon';
import { PaperAirplaneIcon } from './icons/PaperAirplaneIcon';
import { YanaAvatarIcon } from './icons/YanaAvatarIcon';
import { SpeakerWaveIcon } from './icons/SpeakerWaveIcon';
import { StopIcon } from './icons/StopIcon';
import { XIcon } from './icons/XIcon';
import { ChatBubbleIcon } from './icons/ChatBubbleIcon';
import { ShieldIcon } from './icons/ShieldIcon';
import { TrophyIcon } from './icons/TrophyIcon';
import { QuestionMarkCircleIcon } from './icons/QuestionMarkCircleIcon';

// --- Helpers ---

interface UnifiedAIAssistantProps {
    onSubmitQuiz: (data: UserData) => void;
    userEmail?: string | null;
    userId?: string;
    clientId?: string;
    paymentStatus?: 'free' | 'paid';
    hasSubmitted?: boolean;
    variant?: 'floating' | 'inline';
}

type Message = { role: 'user' | 'model'; content: string; };
type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'error' | 'closing';
type AssistantView = 'chat' | 'support';

const UnifiedAIAssistant: React.FC<UnifiedAIAssistantProps> = ({ 
    onSubmitQuiz, 
    userEmail, 
    userId, 
    clientId, 
    paymentStatus = 'free', 
    hasSubmitted = false,
    variant = 'inline' 
}) => {
    const [isExpanded, setIsExpanded] = useState(variant === 'inline');
    const [view, setView] = useState<AssistantView>('chat');
    const [messages, setMessages] = useState<Message[]>([
        { role: 'model', content: "Hi! I'm Yana, your ForgivePath Support Lead. How can I help you maximize your student loan forgiveness today?" }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isQALoading, setIsQALoading] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('idle');
    
    // Support Form State
    const [supportCategory, setSupportCategory] = useState<FeedbackCategory>('question');
    const [supportContent, setSupportContent] = useState('');
    const [successAmount, setSuccessAmount] = useState<string>('');
    const [submittingSupport, setSubmittingSupport] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const streamRef = useRef<MediaStream | null>(null);

    useEffect(() => { if (isExpanded) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, isExpanded]);

    const appLifecycleState = useMemo(() => {
        if (hasSubmitted) return 'submitted';
        if (paymentStatus === 'paid') return 'paid-pending';
        return 'pre-signup';
    }, [hasSubmitted, paymentStatus]);

    const speak = (text: string) => {
        if (!('speechSynthesis' in window)) return;
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        window.speechSynthesis.speak(utterance);
    };

    const escalateToOwner = useCallback(async (reason: string) => {
        const feedback: FeedbackLog = {
            id: `escalation_${Date.now()}`,
            userId: userId || 'anonymous',
            userEmail: userEmail || 'unknown',
            category: 'escalation',
            content: `AUTOMATED ESCALATION: ${reason}`,
            transcript: messages,
            status: 'pending',
            timestamp: new Date().toISOString(),
            appState: appLifecycleState
        };
        await logFeedback(feedback);
        if (userEmail) await sendFeedbackEmail(messages, userEmail);
        setMessages(prev => [...prev, { role: 'model', content: "I've flagged this for the ForgivePath founding team. They will review our conversation and get back to you at your registered email as soon as possible." }]);
        return { success: true };
    }, [messages, userEmail, userId, appLifecycleState]);

    const handleSendText = async () => {
        if (inputValue.trim() === '' || isQALoading) return;
        const userMessage: Message = { role: 'user', content: inputValue };
        setMessages(prev => [...prev, userMessage]);
        const currentMessages = [...messages, userMessage];
        setInputValue('');
        setIsQALoading(true);

        try {
            const systemInstruction = `You are Yana, the ForgivePath Support Manager. 
                    
                    STRICT RULES:
                    1. PRICE: Our service is a ONE-TIME fee of $79. There are NO hidden costs and NO "ongoing plans."
                    2. VALUE: We automatically apply the user to EVERY program they qualify for (PSLF, IDR/SAVE, TPD, etc.) simultaneously. This is our main selling point—we maximize forgiveness by not leaving money on the table.
                    3. FREE: The initial assessment and eligibility check are 100% free.
                    4. GUARANTEE: We offer a 100% Money-Back Guarantee. If the government denies the application we prepared, we refund the $79.
                    5. IDENTITY: We are a private company, NOT the government or a servicer.
                    6. TONE: Professional, authoritative, and helpful. Do not be vague. If you don't know something, use 'escalateToOwner'.
                    
                    Current App State: ${appLifecycleState}.`;

            const text = await aiProxy.generateContent({
                model: 'gemini-1.5-flash', 
                contents: currentMessages.map(msg => ({ 
                    role: msg.role, 
                    parts: [{ text: msg.content }] 
                })),
                config: { 
                    systemInstruction: systemInstruction,
                }
            });

            if (text && text.includes('escalateToOwner')) {
                await escalateToOwner("User reached escalation trigger in conversation.");
            } else {
                setMessages(prev => [...prev, { role: 'model', content: text || "I'm looking into that for you. One moment." }]);
            }
        } catch (error) {
            setMessages(prev => [...prev, { role: 'model', content: "Connection error. Please try again." }]);
        } finally { setIsQALoading(false); }
    };

    const handleSupportSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmittingSupport(true);
        const feedback: FeedbackLog = {
            id: `fb_${Date.now()}`,
            userId: userId || 'anonymous',
            userEmail: userEmail || 'unknown',
            category: supportCategory,
            content: supportContent,
            transcript: messages.length > 1 ? messages : undefined,
            status: 'pending',
            timestamp: new Date().toISOString(),
            appState: appLifecycleState,
            forgivenAmount: successAmount ? parseFloat(successAmount) : undefined
        };
        await logFeedback(feedback);
        if (userEmail) await sendFeedbackEmail(messages, userEmail);
        setSupportContent('');
        setSuccessAmount('');
        setView('chat');
        setMessages(prev => [...prev, { role: 'model', content: "Thank you! Your feedback has been logged and escalated to our founding team." }]);
        setSubmittingSupport(false);
    };

    const startVoice = async () => {
        setMessages(prev => [...prev, { role: 'model', content: "Our voice features are currently undergoing a security upgrade to better protect your data. Please continue using the text chat for now—I'm here to help!" }]);
    };

    const cleanup = useCallback(() => {
        streamRef.current?.getTracks().forEach(t => t.stop());
    }, []);

    const contentClasses = variant === 'inline' 
        ? "w-full bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col"
        : "fixed bottom-6 right-6 z-40 w-[90vw] sm:w-96 bg-white rounded-2xl shadow-2xl flex flex-col border border-gray-200 transition-all h-[60vh] sm:h-[550px] overflow-hidden";

    return (
        <div className={variant === 'inline' ? "my-8" : "pointer-events-none fixed inset-0 z-50"}>
            {isExpanded ? (
                <div className={`${contentClasses} pointer-events-auto`}>
                    <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full shadow-inner"><YanaAvatarIcon className="w-9 h-9" /></div>
                            <div>
                                <h3 className="font-extrabold text-gray-900 text-lg">Support Hub</h3>
                                <div className="flex gap-4 mt-1">
                                    <button onClick={() => setView('chat')} className={`text-[10px] uppercase font-black tracking-widest transition-all ${view === 'chat' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-400 hover:text-gray-600'}`}>Message Center</button>
                                    <button onClick={() => setView('support')} className={`text-[10px] uppercase font-black tracking-widest transition-all ${view === 'support' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-400 hover:text-gray-600'}`}>Official Support</button>
                                </div>
                            </div>
                        </div>
                        {variant === 'floating' && <button onClick={() => setIsExpanded(false)} className="text-gray-400 hover:text-gray-700 p-1"><XIcon className="w-5 h-5" /></button>}
                        {variant === 'inline' && <div className="flex items-center gap-2"><div className={`w-2 h-2 rounded-full ${connectionStatus === 'connected' ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`}></div><span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{connectionStatus === 'connected' ? 'Live Voice Active' : 'Support Hub Ready'}</span></div>}
                    </div>

                    {view === 'chat' ? (
                        <>
                            <div className={`flex-grow p-6 overflow-y-auto bg-gray-50/20 space-y-6 ${variant === 'inline' ? 'min-h-[300px] max-h-[500px]' : ''}`}>
                                {messages.map((msg, i) => (
                                    <div key={i} className={`flex items-end gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        {msg.role === 'model' && <button onClick={() => speak(msg.content)} className="text-gray-400 hover:text-blue-600 p-1.5 mb-1 self-start bg-white rounded-full shadow-sm border border-gray-100"><SpeakerWaveIcon className="w-4 h-4" /></button>}
                                        <div className={`max-w-[80%] px-5 py-3 rounded-2xl text-sm leading-relaxed shadow-sm ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-br-none' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none'}`}>
                                            <p className="whitespace-pre-wrap">{msg.content}</p>
                                        </div>
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </div>
                            <div className="p-4 border-t bg-white">
                                <div className="flex items-center gap-3 p-2 border border-gray-200 rounded-2xl bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                                    <button onClick={() => { if(connectionStatus==='idle') startVoice(); else { cleanup(); setConnectionStatus('idle'); } }} className={`p-3 rounded-xl transition-all ${connectionStatus==='connected'?'bg-red-500 text-white animate-pulse shadow-md':'text-gray-500 hover:bg-gray-200'}`}>
                                        {connectionStatus==='connected'?<StopIcon className="w-5 h-5"/>:<MicrophoneIcon className="w-5 h-5"/>}
                                    </button>
                                    <input ref={inputRef} value={inputValue} onChange={e=>setInputValue(e.target.value)} onKeyPress={e=>e.key==='Enter'&&handleSendText()} placeholder="Ask about pricing, programs, or our guarantee..." className="flex-grow bg-transparent border-0 focus:ring-0 text-sm font-medium"/>
                                    <button onClick={handleSendText} disabled={isQALoading||!inputValue} className="w-11 h-11 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 transition-all flex items-center justify-center shadow-md">
                                        {isQALoading ? <SpinnerIcon className="w-5 h-5 animate-spin"/> : <PaperAirplaneIcon className="w-5 h-5 transform rotate-90"/>}
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className={`flex-grow p-8 overflow-y-auto bg-white ${variant === 'inline' ? 'min-h-[400px]' : ''}`}>
                            <form onSubmit={handleSupportSubmit} className="max-w-2xl mx-auto space-y-6">
                                <div>
                                    <label className="block text-xs font-black text-gray-400 uppercase mb-3 tracking-widest">Support Category</label>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        <button type="button" onClick={()=>setSupportCategory('question')} className={`p-3 border rounded-xl text-xs font-bold flex flex-col items-center gap-2 transition-all ${supportCategory==='question'?'border-blue-500 bg-blue-50 text-blue-700 shadow-sm':'text-gray-500 hover:bg-gray-50'}`}><QuestionMarkCircleIcon className="w-5 h-5"/> Question</button>
                                        <button type="button" onClick={()=>setSupportCategory('bug')} className={`p-3 border rounded-xl text-xs font-bold flex flex-col items-center gap-2 transition-all ${supportCategory==='bug'?'border-red-500 bg-red-50 text-red-700 shadow-sm':'text-gray-500 hover:bg-gray-50'}`}><ShieldIcon className="w-5 h-5"/> Bug</button>
                                        <button type="button" onClick={()=>setSupportCategory('suggestion')} className={`p-3 border rounded-xl text-xs font-bold flex flex-col items-center gap-2 transition-all ${supportCategory==='suggestion'?'border-purple-500 bg-purple-50 text-purple-700 shadow-sm':'text-gray-500 hover:bg-gray-50'}`}><PaperAirplaneIcon className="w-5 h-5"/> Idea</button>
                                        {(hasSubmitted || paymentStatus === 'paid') && <button type="button" onClick={()=>setSupportCategory('success')} className={`p-3 border rounded-xl text-xs font-bold flex flex-col items-center gap-2 transition-all ${supportCategory==='success'?'border-green-500 bg-green-50 text-green-700 shadow-sm':'text-gray-500 hover:bg-gray-50'}`}><TrophyIcon className="w-5 h-5"/> Story</button>}
                                    </div>
                                </div>
                                {supportCategory === 'success' && (
                                    <div className="animate-fade-in bg-green-50 p-4 rounded-xl border border-green-100">
                                        <label className="block text-xs font-bold text-green-700 uppercase mb-2">Total Amount Forgiven ($)</label>
                                        <input type="number" value={successAmount} onChange={e=>setSuccessAmount(e.target.value)} className="w-full p-3 border border-green-200 rounded-lg text-sm font-bold text-green-900 focus:ring-green-500" placeholder="e.g. 50000" required/>
                                    </div>
                                )}
                                <div>
                                    <label className="block text-xs font-black text-gray-400 uppercase mb-3 tracking-widest">Message Details</label>
                                    <textarea value={supportContent} onChange={e=>setSupportContent(e.target.value)} rows={5} className="w-full p-4 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all" placeholder={supportCategory==='success'?'Tell us your success story!':'How can our team help you?'} required/>
                                </div>
                                <button type="submit" disabled={submittingSupport} className="w-full bg-blue-600 text-white font-black py-4 rounded-2xl hover:bg-blue-700 transition-all shadow-lg flex items-center justify-center gap-3 uppercase tracking-widest text-sm">
                                    {submittingSupport ? <SpinnerIcon className="w-6 h-6 animate-spin"/> : 'Escalate to Founding Team'}
                                </button>
                                <p className="text-[10px] text-gray-400 text-center font-bold uppercase tracking-tighter">Your request and recent history will be sent directly to the founding team.</p>
                            </form>
                        </div>
                    )}
                </div>
            ) : variant === 'floating' ? (
                <button onClick={() => setIsExpanded(true)} className="w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full shadow-lg hover:scale-105 transition-all flex items-center justify-center text-white pointer-events-auto">
                    <ChatBubbleIcon className="w-7 h-7" />
                </button>
            ) : null}
        </div>
    );
};
export default UnifiedAIAssistant;
